import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, Star, Send, Users, Calendar, Zap, RefreshCw } from 'lucide-react';
import { getLocalDate, getLocalDateString, getLocalMidnight, getLocalDateStringFormatted, getLocalTodayDateString } from '../lib/utils';
import CustomDatePicker from '../components/ui/custom-date-picker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAST_SUBMISSION_DAYS_LIMIT = 3;

const getMinDateString = (daysBack) => {
  const date = new Date();
  date.setDate(date.getDate() - daysBack);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Tasks() {
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedChild, setSelectedChild] = useState(searchParams.get('child') || '');
  const [selectedDate, setSelectedDate] = useState(() => getLocalTodayDateString());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, task: null });

  // Initial data fetch - only run once on mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const tasksUrl = selectedDate === getLocalTodayDateString()
          ? `${API_URL}/api/tasks`
          : `${API_URL}/api/tasks?date=${selectedDate}`;
          
        const [childrenRes, tasksRes, submissionsRes] = await Promise.all([
          fetch(`${API_URL}/api/children`, {
            credentials: 'include',
            headers: getAuthHeaders()
          }),
          fetch(tasksUrl, {
            credentials: 'include',
            headers: getAuthHeaders()
          }),
          fetch(`${API_URL}/api/submissions`, {
            credentials: 'include',
            headers: getAuthHeaders()
          })
        ]);

        if (isMounted) {
          if (childrenRes.ok) {
            const childrenData = await childrenRes.json();
            setChildren(childrenData);
            if (!selectedChild && childrenData.length > 0) {
              setSelectedChild(childrenData[0].child_id);
            }
          }
          if (tasksRes.ok) setTasks(await tasksRes.json());
          if (submissionsRes.ok) setSubmissions(await submissionsRes.json());
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch data:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [getAuthHeaders, selectedChild, selectedDate]);

  // Refetch tasks when selected date changes
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const tasksUrl = selectedDate === getLocalTodayDateString()
          ? `${API_URL}/api/tasks`
          : `${API_URL}/api/tasks?date=${selectedDate}`;
        
        const res = await fetch(tasksUrl, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        
        if (res.ok) {
          setTasks(await res.json());
        }
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
      }
    };

    fetchTasks();
  }, [selectedDate, getAuthHeaders]);

  // Separate effect to handle URL param child selection without refetching everything
  useEffect(() => {
    // Only fetch children if we have a URL param child but no children data
    const urlChild = searchParams.get('child');
    if (urlChild && children.length === 0) {
      fetch(`${API_URL}/api/children`, {
        credentials: 'include',
        headers: getAuthHeaders()
      }).then(res => {
        if (res.ok) return res.json();
      }).then(childrenData => {
        if (childrenData) {
          setChildren(childrenData);
        }
      }).catch(console.error);
    }
  }, [children.length, getAuthHeaders, searchParams]); // Removed selectedChild from dependencies

// Memoized Task Card Component
const TaskCard = memo(({ task, index, getTaskStatus, canSubmitTask, getTimeRemaining, getStatusBadge, getButtonText, submitting, handleConfirmSubmission }) => {
  const status = getTaskStatus(task.task_id);
  const canSubmit = canSubmitTask(task);
  const timeRemaining = getTimeRemaining(task);
  
  return (
    <Card 
      key={task.task_id}
      className="bg-white rounded-2xl border-l-4 border-l-[#08513F] border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`task-card-${task.task_id}`}
    >
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-[#264653] font-outfit">
                {task.name}
              </h3>
              {getStatusBadge(status)}
              <Badge className="bg-[#08513F]/10 text-[#08513F] rounded-full">
                <RefreshCw className="w-3 h-3 mr-1" />
                Daily
              </Badge>
              {task.requires_approval ? (
                <Badge variant="outline" className="rounded-full text-yellow-600 border-yellow-300">
                  <Clock className="w-3 h-3 mr-1" />
                  Needs Approval
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-full text-green-600 border-green-300">
                  <Zap className="w-3 h-3 mr-1" />
                  Auto-Approved
                </Badge>
              )}
            </div>
            <p className="text-[#6B7280] mb-3">{task.description}</p>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1 text-[#D4AF37] font-semibold">
                <Star className="w-4 h-4" />
                {task.points} pts
              </span>
              {task.gender_target !== 'All' && (
                <Badge variant="outline" className="rounded-full">
                  {task.gender_target}
                </Badge>
              )}
              {timeRemaining && (
                <span className="flex items-center gap-1 text-[#6B7280] text-sm">
                  <Clock className="w-3 h-3" />
                  {timeRemaining}
                </span>
              )}
            </div>
          </div>
          
          {status !== 'rejected' && (
            <Button
              onClick={() => handleConfirmSubmission(task)}
              disabled={!canSubmit || submitting[task.task_id]}
              className={`rounded-xl px-6 ${
                canSubmit 
                  ? task.requires_approval
                    ? 'bg-[#E76F51] hover:bg-[#D65A3C] text-white'
                    : 'bg-[#2A9D8F] hover:bg-[#238B7F] text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid={`submit-task-${task.task_id}`}
            >
              {submitting[task.task_id] ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {canSubmit && <Send className="w-4 h-4 mr-2" />}
                  {getButtonText(task, status)}
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

TaskCard.displayName = 'TaskCard';

// Memoized Special Task Card Component  
const SpecialTaskCard = memo(({ task, index, getTaskStatus, canSubmitTask, getTimeRemaining, getStatusBadge, getButtonText, submitting, handleConfirmSubmission }) => {
  const status = getTaskStatus(task.task_id);
  const canSubmit = canSubmitTask(task);
  const timeRemaining = getTimeRemaining(task);
  
  return (
    <Card 
      key={task.task_id}
      className="bg-white rounded-2xl border-l-4 border-l-[#D4AF37] border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`task-card-${task.task_id}`}
    >
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-[#264653] font-outfit">
                {task.name}
              </h3>
              {getStatusBadge(status)}
              <Badge className="bg-[#D4AF37]/20 text-[#B5952F] rounded-full">
                <Star className="w-3 h-3 mr-1" />
                One-Time
              </Badge>
              {task.requires_approval ? (
                <Badge variant="outline" className="rounded-full text-yellow-600 border-yellow-300">
                  <Clock className="w-3 h-3 mr-1" />
                  Needs Approval
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-full text-green-600 border-green-300">
                  <Zap className="w-3 h-3 mr-1" />
                  Auto-Approved
                </Badge>
              )}
            </div>
            <p className="text-[#6B7280] mb-3">{task.description}</p>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1 text-[#D4AF37] font-semibold">
                <Star className="w-4 h-4" />
                {task.points} pts
              </span>
              {task.deadline_date && (
                <span className="flex items-center gap-1 text-[#6B7280] text-sm">
                  <Calendar className="w-3 h-3" />
                  Deadline: {getLocalDateStringFormatted(task.deadline_date)}
                </span>
              )}
              {timeRemaining && (
                <span className={`flex items-center gap-1 text-sm ${
                  timeRemaining === 'Expired' ? 'text-red-500' : 'text-[#E76F51]'
                }`}>
                  <Clock className="w-3 h-3" />
                  {timeRemaining}
                </span>
              )}
            </div>
          </div>
          
          {status !== 'rejected' && timeRemaining !== 'Expired' && (
            <Button
              onClick={() => handleConfirmSubmission(task)}
              disabled={!canSubmit || submitting[task.task_id]}
              className={`rounded-xl px-6 ${
                canSubmit 
                  ? task.requires_approval
                    ? 'bg-[#D4AF37] hover:bg-[#B5952F] text-white'
                    : 'bg-[#2A9D8F] hover:bg-[#238B7F] text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid={`submit-task-${task.task_id}`}
            >
              {submitting[task.task_id] ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {canSubmit && <Send className="w-4 h-4 mr-2" />}
                  {getButtonText(task, status)}
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

SpecialTaskCard.displayName = 'SpecialTaskCard';

  const getSelectedChild = useCallback(() => children.find(c => c.child_id === selectedChild), [children, selectedChild]);

  const getTaskStatus = useCallback((taskId) => {
    const childSubmissions = submissions.filter(s => s.child_id === selectedChild);
    if (childSubmissions.length === 0) return null;
    
    const task = tasks.find(t => t.task_id === taskId);
    
    if (task?.frequency === 'daily') {
      const selectedDateSubmission = childSubmissions.find(
        s => s.task_id === taskId && getLocalDateString(s.submission_date) === selectedDate
      );
      return selectedDateSubmission?.status || null;
    }
    
    // One-time task - get the latest submission for this specific task
    const taskSubmissions = childSubmissions.filter(s => s.task_id === taskId);
    if (taskSubmissions.length === 0) return null;
    
    const sortedSubmissions = taskSubmissions.sort(
      (a, b) => new Date(b.submission_date) - new Date(a.submission_date)
    );
    return sortedSubmissions[0]?.status || null;
  }, [submissions, selectedChild, tasks, selectedDate]);

  const getTimeRemaining = (task) => {
    const now = new Date();
    const isPastDate = selectedDate < getLocalTodayDateString();
    
    // For past dates, don't show time remaining or deadlines
    if (isPastDate) {
      return null;
    }
    
    if (task.frequency === 'daily') {
      const estNow = getLocalDate();
      const midnight = getLocalMidnight();
      const diff = midnight - estNow;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m left today`;
    } else if (task.deadline_date) {
      const deadline = new Date(task.deadline_date);
      deadline.setHours(23, 59, 59, 999);
      const diff = deadline - now;
      
      if (diff < 0) return 'Expired';
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) return `${days}d ${hours}h left`;
      return `${hours}h left`;
    }
    
    return null;
  };
    
  const handleConfirmSubmission = (task) => {
    setConfirmDialog({ open: true, task });
  };

  const handleSubmit = async (taskId) => {
    if (!selectedChild) {
      toast.error('Please select a child first');
      return;
    }

    setSubmitting({ ...submitting, [taskId]: true });

    try {
      const isToday = selectedDate === getLocalTodayDateString();
      const requestBody = {
        task_id: taskId,
        child_id: selectedChild
      };

      if (!isToday) {
        const [year, month, day] = selectedDate.split('-').map(Number);
        const submissionDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        requestBody.submission_date = submissionDate.toISOString();
      }

      const response = await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const task = tasks.find(t => t.task_id === taskId);
        if (task?.requires_approval === false) {
          toast.success(`Task "${task?.name}" auto-approved! +${task?.points} points!`);
        } else {
          toast.success(`Task "${task?.name}" submitted for approval!`);
        }
        
        // Refresh submissions
        const submissionsRes = await fetch(`${API_URL}/api/submissions`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (submissionsRes.ok) {
          setSubmissions(await submissionsRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit task');
      }
    } catch (error) {
      toast.error('Failed to submit task');
    } finally {
      setSubmitting({ ...submitting, [taskId]: false });
    }
  };

  const getFilteredTasks = useCallback(() => {
    const child = getSelectedChild();
    if (!child) return tasks;
    
    return tasks.filter(task => {
      const genderMatch = task.gender_target === 'All' || task.gender_target === child.gender;
      if (!genderMatch) return false;

      const childAge = child.age;
      if (task.min_age && childAge < task.min_age) return false;
      if (task.max_age && childAge > task.max_age) return false;

      // Filter expired tasks on client side too (belt-and-suspenders)
      if (task.end_date && task.end_date < getLocalTodayDateString()) return false;

      return true;
    });
  }, [tasks, getSelectedChild]);

  const getStatusBadge = useCallback((status) => {
    switch (status) {
      case 'submitted':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 rounded-full px-3">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800 rounded-full px-3">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 rounded-full px-3">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  }, []);

  const canSubmitTask = useCallback((task) => {
    const status = getTaskStatus(task.task_id);
    
    // Can't submit if already submitted or approved
    if (status === 'submitted' || status === 'approved') return false;
    
    // For daily tasks, rejected means can try again tomorrow (but same day = no)
    if (task.frequency === 'daily' && status === 'rejected') {
      return false; // Cannot resubmit rejected daily task same day
    }
    
    // Check deadline for one-time tasks based on selected date
    if (task.frequency === 'one_time' && task.deadline_date) {
      const deadline = new Date(task.deadline_date);
      deadline.setHours(23, 59, 59, 999);
      const selectedDateTime = new Date(selectedDate);
      selectedDateTime.setHours(23, 59, 59, 999);
      if (selectedDateTime > deadline) return false;
    }
    
    return true;
  }, [getTaskStatus, selectedDate]);

  const getButtonText = useCallback((task, status) => {
    if (status === 'submitted') return 'Pending Approval';
    if (status === 'approved') return 'Completed';
    if (status === 'rejected') return 'Rejected';
    
    if (!task.requires_approval) return 'Complete (Auto)';
    return 'Submit';
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Tasks</h1>
            <p className="text-[#6B7280] mt-1">Complete Islamic challenges and earn points</p>
          </div>

          {children.length > 0 && (
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#6B7280]" />
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger className="w-48 rounded-xl" data-testid="child-selector">
                  <SelectValue placeholder="Select child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map((child) => (
                    <SelectItem key={child.child_id} value={child.child_id}>
                      {child.first_name} {child.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <CustomDatePicker
                value={selectedDate}
                min={getMinDateString(PAST_SUBMISSION_DAYS_LIMIT)}
                max={getLocalTodayDateString()}
                onChange={setSelectedDate}
              />
            </div>
          )}
        </div>

        {/* No Children */}
        {children.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Children Added</h2>
            <p className="text-[#6B7280] mb-6">Please add a child first to submit tasks for them.</p>
            <Button
              onClick={() => window.location.href = '/children'}
              className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-8"
            >
              Add Child
            </Button>
          </Card>
        ) : (
          <>
            {/* Daily Tasks */}
            <div>
              <h2 className="text-xl font-bold text-[#264653] font-outfit mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#08513F] flex items-center justify-center">
                  <RefreshCw className="w-4 h-4 text-white" />
                </div>
                Daily Tasks
                {selectedDate !== getLocalTodayDateString() && (
                  <span className="text-sm font-normal text-[#6B7280]">
                    ({new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})
                  </span>
                )}
              </h2>
              
              <div className="grid gap-4">
                {getFilteredTasks().filter(t => t.frequency === 'daily').map((task, index) => (
                  <TaskCard 
                    key={task.task_id}
                    task={task}
                    index={index}
                    getTaskStatus={getTaskStatus}
                    canSubmitTask={canSubmitTask}
                    getTimeRemaining={getTimeRemaining}
                    getStatusBadge={getStatusBadge}
                    getButtonText={getButtonText}
                    submitting={submitting}
                    handleConfirmSubmission={handleConfirmSubmission}
                  />
                ))}
                
                {getFilteredTasks().filter(t => t.frequency === 'daily').length === 0 && (
                  <p className="text-center text-[#6B7280] py-8">No daily tasks available</p>
                )}
              </div>
            </div>

            {/* One-Time Tasks */}
            {getFilteredTasks().filter(t => t.frequency === 'one_time').length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-[#264653] font-outfit mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                    <Star className="w-4 h-4 text-white" />
                  </div>
                  Special Challenges
                </h2>
                
                <div className="grid gap-4">
                  {getFilteredTasks().filter(t => t.frequency === 'one_time').map((task, index) => (
                    <SpecialTaskCard 
                      key={task.task_id}
                      task={task}
                      index={index}
                      getTaskStatus={getTaskStatus}
                      canSubmitTask={canSubmitTask}
                      getTimeRemaining={getTimeRemaining}
                      getStatusBadge={getStatusBadge}
                      getButtonText={getButtonText}
                      submitting={submitting}
                      handleConfirmSubmission={handleConfirmSubmission}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Confirm Task Submission Dialog */}
        {confirmDialog.open && (
          <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Submit {confirmDialog.task?.name}</DialogTitle>
                <DialogDescription>
                  Submit {confirmDialog.task?.name} for {getSelectedChild()?.first_name}? This will award {confirmDialog.task?.points} points.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button 
                  onClick={() => {
                    handleSubmit(confirmDialog.task.task_id);
                    setConfirmDialog({ open: false, task: null });
                  }}
                  disabled={submitting[confirmDialog.task.task_id]}
                >
                  Submit
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setConfirmDialog({ open: false, task: null })}
                >
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
